"use client";

import { Globe, Menu, Moon, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, {
  Fragment,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import useTracking from "@/app/common/utils/useTracking";
import { PAGES_LINKS } from "@/app/utils/constants/constants";
import { useRouter } from "@/i18n/navigation";
import ButtonSignIn from "./Loader/ButtonSignIn";
import { getFiatCurrencies } from "@/app/constants/Currencies";
import { CurrencyContext } from "@/app/context/Search/CurrencyContext";
import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import { useTheme } from "next-themes";
import { CurrencyModal } from "./CurrencyModal";
import CurrencyImage from "./CurrencyModal/CurrencyImage";
import ThemeSwitch from "./ThemeSwitch";
import { getPartnerCode } from "@/app/utils/services/service/localStorageService";
import { LangSwitcher } from "./LangSwitcher/LangSwitcher";
import { useOnClickOutside } from "@/app/hooks/useOnClickOutside";
import { useTranslations } from "next-intl";

function HeroHeader({ logo }: { logo?: string }) {
  const { push } = useRouter();
  const tHeader = useTranslations("Header");
  const t = useTranslations();

  const [toggle, setToggle] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showLocale, setShowLocale] = useState(false);
  const [selectedCurrencyIcon, setSelectedCurrencyIcon] = useState<ReactNode>();
  const { userDetails, loader } = useWalletService();
  const [languageDropdown, setLanguageDropdown] = useState(false);
  const [showPartnerLogo, setShowPartnerLogo] = useState(true);
  const [mounted, setMounted] = useState(false);
  const { setTheme, resolvedTheme } = useTheme();
  const languageDropdownRef = useOnClickOutside(() =>
    setLanguageDropdown(false)
  );

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    setShowPartnerLogo(true);
  }, [logo]);

  const { walletId, points, isCBOneAttested } = userDetails;
  const { currencies, currency, setUpdateCurrency, rate } =
    useContext(CurrencyContext);

  useTracking();

  const handleSignIn = () => {
    if (walletId) push("/account");
  };

  useEffect(() => {
    if (!currencies.length) return;

    const savedCurrency = currencies.find(
      (item) => item.value === currency.value
    );

    if (savedCurrency) {
      if (savedCurrency.type === "FIAT") {
        setSelectedCurrencyIcon(
          getFiatCurrencies(t)?.find((c) => c?.label == savedCurrency?.value)
            ?.icon
        );
      } else {
        setSelectedCurrencyIcon(
          <CurrencyImage symbol={savedCurrency?.value} />
        );
      }
    } else {
      setSelectedCurrencyIcon(
        <Image
          src="/assets/v2/icons/flags/us.svg"
          height={32}
          alt="icon"
          width={32}
        />
      );
    }
  }, [currencies.length, rate]);

  const partnerCode = getPartnerCode();
  const isCoinbasePartner = partnerCode === "coinbase";
  const normalizedLogo = logo?.trim();

  return (
    <div className="pt-3.5 pb-5 md:pt-14 md:pb-20">
      <div className="container flex mx-auto items-center !sm:px-4 !p-0">
        <div className="flex items-center flex-auto">
          <div className="flex gap-2 sm:gap-4 items-center">
            <a href="/">
              <Image
                src="/assets/v2/sleap-staynex-white-logo.svg"
                className="sm:h-16 h-14 sm:w-auto sm:max-w-[192px] max-w-[136px]"
                width={192}
                height={60}
                alt="Sleap Logo"
              />
            </a>

            {isCoinbasePartner ? (
              <Fragment>
                <div className="sm:h-10 h-6 w-[1px] bg-white"></div>
                <Image
                  src="/assets/v2/cb1-logo-light.svg"
                  className="h-6 sm:w-auto sm:max-w-[156px] max-w-[106px]"
                  width={156}
                  height={40}
                  alt="Coinbase Partner Logo"
                />
              </Fragment>
            ) : normalizedLogo && showPartnerLogo ? (
              <Fragment>
                <div className="sm:h-10 h-6 w-[1px] bg-white"></div>
                <Image
                  src={normalizedLogo}
                  className="h-10 sm:w-auto sm:max-w-[156px] max-w-[106px]"
                  width={156}
                  height={40}
                  alt="Partner Logo"
                  onError={() => setShowPartnerLogo(false)}
                />
              </Fragment>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          <div className="hidden lg:block">
            <ThemeSwitch isHomePage={true} />
          </div>
          {currencies.length ? (
            <span
              className="cursor-pointer hidden lg:flex text-white items-center rounded-full gap-x-1 px-4 py-3 text-sm font-semibold bg-[rgba(255,255,255,0.35)] hover:bg-[rgba(255,255,255,0.65)] transition dark:bg-[rgba(139,94,247,0.66)] dark:hover:bg-[rgba(139,94,247,0.76)]"
              onClick={() => setShowCurrencyModal(true)}
            >
              {currency?.label}
            </span>
          ) : (
            ""
          )}
          <div
            ref={languageDropdownRef as any}
            onClick={() => {
              setToggle(false);
              setLanguageDropdown(!languageDropdown);
            }}
            className="relative p-2 cursor-pointer lg:block hidden"
          >
            {languageDropdown ? (
              <X className="text-white cursor-pointer h-5 w-5" />
            ) : (
              <Globe className="h-5 w-5" color="#fff" />
            )}

            <div
              className={`${
                languageDropdown ? "block" : "hidden"
              } absolute top-9 right-0 bg-white dark:bg-sleap-gray-950 w-[280px] border-2 border-sleap-off-white dark:border-sleap-gray-850 rounded-xl p-4 z-50`}
            >
              <ul className="dark:text-white text-sleap-gray-800 flex flex-col gap-2 justify-center mb-0 text-base font-medium">
                <li
                  className="md:block hidden"
                  onClick={() => setLanguageDropdown(false)}
                >
                  <LangSwitcher />
                </li>
              </ul>
            </div>
          </div>

          <ButtonSignIn
            walletId={walletId}
            handleSignIn={handleSignIn}
            loader={loader}
            points={points}
            isCBOneAttested={isCBOneAttested}
          />

          {showCurrencyModal && (
            <CurrencyModal
              setOpenModal={setShowCurrencyModal}
              openModal={showCurrencyModal}
              currency={currency}
              setUpdateCurrency={setUpdateCurrency}
              currencies={currencies}
            />
          )}

          <LangSwitcher
            showLocale={showLocale}
            setShowLocale={setShowLocale}
            isModal={true}
          />

          <div onClick={() => setToggle(!toggle)} className="block relative">
            {toggle ? (
              <X className="text-white cursor-pointer" />
            ) : (
              <Menu className="text-white cursor-pointer" />
            )}

            <div
              className={`${
                toggle ? "block" : "hidden"
              } absolute top-9 right-[0px] bg-white dark:bg-sleap-gray-950 w-[196px] border-2 border-sleap-off-white dark:border-sleap-gray-850 rounded-xl py-4 px-2 z-50`}
            >
              <ul className="dark:text-white text-sleap-gray-800 flex flex-col gap-3 justify-center mb-0 text-base font-medium">
                <li
                  onClick={() => setToggle(false)}
                  className="block lg:hidden"
                >
                  {currencies.length ? (
                    <span
                      className="cursor-pointer flex dark:text-white text-sleap-gray-800 items-center gap-x-1 xs:px-4 px-4 py-2"
                      onClick={() => setShowCurrencyModal(true)}
                    >
                      {selectedCurrencyIcon} - {currency?.label}
                    </span>
                  ) : (
                    ""
                  )}
                </li>
                <li
                  className="block lg:hidden"
                  onClick={() => {
                    setShowLocale(true);
                    setToggle(false);
                  }}
                >
                  <LangSwitcher
                    showLocale={showLocale}
                    setShowLocale={setShowLocale}
                  />
                </li>
                <li
                  onClick={() => setToggle(false)}
                  className="block lg:hidden"
                >
                  {!mounted && (
                    <span className="w-9 h-9 bg-transparent block"></span>
                  )}
                  {mounted && (
                    <span
                      onClick={() =>
                        setTheme(resolvedTheme === "dark" ? "light" : "dark")
                      }
                      className="cursor-pointer flex gap-3 px-4 py-2"
                    >
                      <Moon className="text-dark dark:text-white" />
                      <span>
                        {resolvedTheme === "dark"
                          ? tHeader("theme.light")
                          : tHeader("theme.dark")}
                      </span>
                    </span>
                  )}
                </li>

                <li className="block lg:hidden">
                  <hr className="h-[1px] bg-gray-100 dark:bg-sleap-gray-700 border-0" />
                </li>

                {/* MENU LINKS */}
                <li onClick={() => setToggle(false)}>
                  <Link
                    className="dark:text-white text-sleap-gray-800 px-4 py-2"
                    href={PAGES_LINKS.POINTS}
                    target="_blank"
                  >
                    {tHeader("menu.points")}
                  </Link>
                </li>
                <li onClick={() => setToggle(false)}>
                  <Link
                    className="dark:text-white text-sleap-gray-800 px-4 py-2"
                    href={PAGES_LINKS.BENEFITS}
                    target="_blank"
                  >
                    {tHeader("menu.nftBenefits")}
                  </Link>
                </li>
                <li onClick={() => setToggle(false)}>
                  <Link
                    className="dark:text-white text-sleap-gray-800 px-4 py-2"
                    href={PAGES_LINKS.NFT}
                    target="_blank"
                  >
                    {tHeader("menu.nftCollection")}
                  </Link>
                </li>
                <li onClick={() => setToggle(false)}>
                  <Link
                    className="dark:text-white text-sleap-gray-800 px-4 py-2"
                    href={PAGES_LINKS.ABOUT}
                    target="_blank"
                  >
                    {tHeader("menu.about")}
                  </Link>
                </li>
                <li onClick={() => setToggle(false)}>
                  <Link
                    className="dark:text-white text-sleap-gray-800 px-4 py-2"
                    href={PAGES_LINKS.HELP}
                    target="_blank"
                  >
                    {tHeader("menu.help")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroHeader;
